import { TCreatorApplicationDetail } from '@/types/CreatorApplication.types';
import { getUserRolesDisplay } from '@/utils/stringUtils';
import { Text, VStack } from '@chakra-ui/react';
import { Link } from '@tanstack/react-router';
import React from 'react';
import {
  CreatorApplicationStatusBadge,
  formatCreatorApplicationDate,
  getCreatorApplicantTypeLabel,
} from '../creatorApplicationDisplay';
import { InfoRow, SectionCard } from './SectionLayout';

interface Props {
  data: TCreatorApplicationDetail;
}

const DetailsTab = ({ data }: Props) => {
  const fullName = `${data.applicantName?.firstName ?? ''} ${data.applicantName?.lastName ?? ''}`.trim() || '/';
  const existingUser = data.existingUser;

  return (
    <VStack align="stretch" width="100%" gap="20px">
      <SectionCard title="Applicant">
        <InfoRow label="Name">{fullName}</InfoRow>
        <InfoRow label="Email">{data.applicantEmail ?? '/'}</InfoRow>
        <InfoRow label="Applicant type">{getCreatorApplicantTypeLabel(data.applicantType)}</InfoRow>
        <InfoRow label="Email verified">{formatCreatorApplicationDate(data.emailVerifiedAt)}</InfoRow>
      </SectionCard>

      <SectionCard title="Status">
        <InfoRow label="Status">
          <CreatorApplicationStatusBadge status={data.status} />
        </InfoRow>
        <InfoRow label="Submitted">{formatCreatorApplicationDate(data.submittedAt)}</InfoRow>
        <InfoRow label="Decided">{formatCreatorApplicationDate(data.decidedAt)}</InfoRow>
        {data.decisionNotes && <InfoRow label="Decision notes">{data.decisionNotes}</InfoRow>}
      </SectionCard>

      <SectionCard title="Social Media Accounts">
        {data.profile.socialLinks?.map((socialLink) => (
          <InfoRow key={`${socialLink.platform}-${socialLink.url}`} label={socialLink.platform}>
            <Text
              as="a"
              href={socialLink.url}
              target="_blank"
              rel="noopener noreferrer"
              color="text.mediumBlue"
              fontWeight={600}
              textDecoration="underline"
              _hover={{ color: 'primary.500' }}
            >
              {socialLink.url}
            </Text>
          </InfoRow>
        ))}
      </SectionCard>

      <SectionCard title="Organization Member">
        {existingUser ? (
          <>
            <InfoRow label="Roles">
              {existingUser.roles?.length ? getUserRolesDisplay(existingUser.roles) : '/'}
            </InfoRow>
            <InfoRow label="Has organization">{existingUser.hasOrganization ? 'Yes' : 'No'}</InfoRow>
            {existingUser.organizationId && (
              <InfoRow label="Organization ID">
                <Link to="/organizations/$organizationId" params={{ organizationId: existingUser.organizationId }}>
                  <Text
                    as="span"
                    color="text.mediumBlue"
                    fontWeight={600}
                    textDecoration="underline"
                    _hover={{ color: 'primary.500' }}
                  >
                    {existingUser.organizationId}
                  </Text>
                </Link>
              </InfoRow>
            )}
          </>
        ) : (
          <InfoRow label="Account">Not an organization member yet</InfoRow>
        )}
      </SectionCard>
    </VStack>
  );
};

export default DetailsTab;
